import type * as MonacoApi from "monaco-editor/editor";
import * as Y from "yjs";

/** Transaction origin for edits made through this binding (used by undo to track local edits). */
export const LOCAL_EDIT_ORIGIN = Symbol("codeshare:local-edit");

export interface TextBinding {
  dispose(): void;
}

/**
 * Keeps a Y.Text and a Monaco model identical in both directions. Remote changes are applied as
 * model edits (outside Monaco's undo stack); local typing becomes Y.Text operations. A flag stops
 * each side from echoing the other.
 */
export function bindText(ytext: Y.Text, model: MonacoApi.editor.ITextModel): TextBinding {
  const doc = ytext.doc;
  if (!doc) throw new Error("Y.Text must belong to a Y.Doc");
  let applyingRemote = false;
  let applyingLocal = false;

  // Start from the shared text.
  if (model.getValue() !== ytext.toString()) {
    applyingRemote = true;
    model.setValue(ytext.toString());
    applyingRemote = false;
  }

  const onYText = (event: Y.YTextEvent, transaction: Y.Transaction) => {
    if (applyingLocal || transaction.origin === LOCAL_EDIT_ORIGIN) return;
    const edits: MonacoApi.editor.IIdentifiedSingleEditOperation[] = [];
    let index = 0;
    for (const op of event.delta) {
      if (op.retain !== undefined) {
        index += op.retain;
      } else if (op.insert !== undefined) {
        const position = model.getPositionAt(index);
        const text = typeof op.insert === "string" ? op.insert : "";
        edits.push({
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
          text,
        });
        index += text.length;
      } else if (op.delete !== undefined) {
        const start = model.getPositionAt(index);
        const end = model.getPositionAt(index + op.delete);
        edits.push({
          range: {
            startLineNumber: start.lineNumber,
            startColumn: start.column,
            endLineNumber: end.lineNumber,
            endColumn: end.column,
          },
          text: "",
        });
        index += op.delete;
      }
    }
    applyingRemote = true;
    try {
      model.applyEdits(edits);
    } finally {
      applyingRemote = false;
    }
  };
  ytext.observe(onYText);

  const contentListener = model.onDidChangeContent((event) => {
    if (applyingRemote) return;
    applyingLocal = true;
    try {
      doc.transact(() => {
        // Apply from the end so earlier offsets stay valid.
        const changes = [...event.changes].sort((a, b) => b.rangeOffset - a.rangeOffset);
        for (const change of changes) {
          if (change.rangeLength > 0) ytext.delete(change.rangeOffset, change.rangeLength);
          if (change.text) ytext.insert(change.rangeOffset, change.text);
        }
      }, LOCAL_EDIT_ORIGIN);
    } finally {
      applyingLocal = false;
    }
  });

  return {
    dispose() {
      ytext.unobserve(onYText);
      contentListener.dispose();
    },
  };
}

/** Collaborative undo: only this user's edits, merged into word-sized steps. */
export function createUndoManager(ytext: Y.Text): Y.UndoManager {
  return new Y.UndoManager(ytext, {
    trackedOrigins: new Set([LOCAL_EDIT_ORIGIN]),
    captureTimeout: 500,
  });
}
